import { useState } from "react";
import { api } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Badge,
  Button,
  Card,
  CodeBlock,
  FieldShell,
  Input,
  typographyClass,
} from "../ui-kit";
import type { TaskResponse } from "../api/types";

export function TasksPage() {
  const { t } = useI18n();
  const [taskId, setTaskId] = useState("");
  const [task, setTask] = useState<TaskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    const id = Number(taskId);
    if (!Number.isFinite(id) || id <= 0) return;
    setLoading(true);
    setError(null);
    try {
      setTask(await api.task(id));
    } catch (cause) {
      setTask(null);
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!task) return;
    try {
      await api.cancelTask(task.task_id);
      await check();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  return (
    <Card>
      <h1 className={typographyClass("header-24-semibold")}>{t("task.title")}</h1>

      <FieldShell label={t("task.id")} htmlFor="task-id">
        <Input
          id="task-id"
          inputMode="numeric"
          value={taskId}
          placeholder="184293"
          onChange={(event) => setTaskId(event.target.value)}
        />
      </FieldShell>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-2-5)" }}>
        <Button loading={loading} onClick={check}>{t("action.check")}</Button>
        {task && <Button variant="danger" onClick={cancel}>{t("action.cancel")}</Button>}
      </div>

      {error && <AlertBanner tone="error" title={t("status.error")}>{error}</AlertBanner>}

      {!task && !error && (
        <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
          {t("task.empty")}
        </p>
      )}

      {task && (
        <>
          <div style={{ display: "flex", gap: "var(--spacing-2)", flexWrap: "wrap" }}>
            <Badge tone={task.status === "done" ? "positive" : task.status === "error" ? "error" : "default"} size="small">
              {task.status}
            </Badge>
            {task.position != null && <Badge tone="outline" size="small">{t("task.position")}: {task.position}</Badge>}
          </div>
          {task.error_message && <AlertBanner tone="error" title={t("task.error")}>{task.error_message}</AlertBanner>}
          <CodeBlock code={JSON.stringify(task, null, 2)} copyLabel={t("action.copy")} />
        </>
      )}
    </Card>
  );
}
